// ResearchDayModal.js
import React, { useState, useEffect } from 'react';
import './ResearchDayModal.css';
import kosLogo from '../assets/logo.svg';
import kmaLogo from '../assets/kma_logo_2025.png';
import kimsLogo from '../assets/kims_logo.png';
import bayerLogo from '../assets/bayer_logo.png';

export const RESEARCH_DAY_SCHEDULE = [
  {
    id: 1,
    time: '8:50 – 9:00',
    title: 'Opening Remarks and Welcome',
    presenter: 'Raed Behbehani',
    type: 'special',
    badge: 'Opening'
  },
  {
    id: 2,
    time: '9:00 – 9:10',
    title: 'Defocus Incorporated Multiple Segments (DIMS) Spectacle Lenses Versus Single-Vision Lenses for Pediatric Myopia: A Systematic Review and Meta-Analysis',
    presenter: 'Yousef Mesaed Al-Shammari',
    type: 'presentation'
  },
  {
    id: 3,
    time: '9:10 – 9:20',
    title: 'Efficacy and Safety of PreserFlo MicroShunt Versus Trabeculectomy in Glaucoma Patients',
    presenter: 'Abdulaziz Alotaibi',
    type: 'presentation'
  },
  {
    id: 4,
    time: '9:20 – 9:30',
    title: 'Impact of Silicone Oil Tamponade on Retinal and Choroidal Structure After Rhegmatogenous Retinal Detachment Surgery',
    presenter: 'Demah Alkhurainej',
    type: 'presentation'
  },
  {
    id: 5,
    time: '9:30 – 9:40',
    title: 'Rate and Predictors of Malignant Transformation in Retinocytoma: A Systematic Review and Meta-Analysis',
    presenter: 'Mohammad Almutairi',
    type: 'presentation'
  },
  {
    id: 6,
    time: '9:40 – 9:50',
    title: 'Donor Tissue Preservation Time, Post-Keratoplasty Endothelial Cell Loss and Graft Survival: A Systematic Review',
    presenter: 'Abdalwahab Alenezy',
    type: 'presentation'
  },
  {
    id: 7,
    time: '9:50 – 10:00',
    title: 'Idebenone versus Gene Therapy in Leber Hereditary Optic Neuropathy: Systematic Review and Indirect Comparative Meta-Analysis',
    presenter: 'Noura F. AlMutairi',
    type: 'presentation'
  },
  {
    id: 8,
    time: '10:00 – 10:20',
    title: 'Coffee Break (20 mins)',
    presenter: 'Networking & Refreshments',
    type: 'break',
    badge: 'Break'
  },
  {
    id: 9,
    time: '10:20 – 10:30',
    title: 'Modified Posterior White-line Advancement Using a Two-Suture Technique: A Retrospective Case Series',
    presenter: 'Farah AlNasser',
    type: 'presentation'
  },
  {
    id: 10,
    time: '10:30 – 10:40',
    title: 'Timing of Customized Photorefractive Keratectomy Combined With Corneal Collagen Cross-Linking in Keratoconus: A Systematic Review and Meta-Analysis',
    presenter: 'Ghazi Alharbi',
    type: 'presentation'
  },
  {
    id: 11,
    time: '10:40 – 10:50',
    title: 'Intracameral Moxifloxacin for Endophthalmitis Prophylaxis After Cataract Surgery: A Systematic Review and Meta-Analysis',
    presenter: 'Rashed A. Alasoosi',
    type: 'presentation'
  },
  {
    id: 12,
    time: '11:00 – 11:10',
    title: 'Timing of Combined Corneal Collagen Cross-Linking and Intracorneal Ring Segments for Keratoconus: A Systematic Review and Meta-analysis',
    presenter: 'Haidar Bonajmah',
    type: 'presentation'
  },
  {
    id: 13,
    time: '11:10 – 11:20',
    title: 'Postoperative Dry Eye Outcomes Following SMILE Versus LASIK: A Systematic Review and Meta-Analysis',
    presenter: 'Ahmed Mohammad',
    type: 'presentation'
  },
  {
    id: 14,
    time: '11:20 – 11:30',
    title: 'Prevalence of Orbital CT Findings in Patients With Orbital Cellulitis at Al-Bahar Eye Center',
    presenter: 'Ali Esmaeil',
    type: 'presentation'
  },
  {
    id: 15,
    time: '11:30 – 11:40',
    title: 'Keratoconus Characteristics and Risk Factors in the Kingdom of Bahrain',
    presenter: 'Nora Mubarak AlGhareeb',
    type: 'presentation'
  },
  {
    id: 16,
    time: '11:45 – 12:00',
    title: 'Sponsor Symposium',
    presenter: 'Bayer',
    type: 'symposium',
    badge: 'Sponsored Session',
    hasBayerLogo: true
  },
  {
    id: 17,
    time: '12:35 – 12:55',
    title: 'Awards Ceremony',
    presenter: 'Program Committee',
    type: 'special',
    badge: 'Awards'
  }
];

const ResearchDayModal = ({ isOpen, onClose }) => {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Close on ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredSchedule = RESEARCH_DAY_SCHEDULE.filter((item) => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'presentations' && item.type === 'presentation') ||
      (filter === 'special' && (item.type === 'special' || item.type === 'symposium' || item.type === 'break'));

    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !query ||
      item.title.toLowerCase().includes(query) ||
      item.presenter.toLowerCase().includes(query) ||
      item.time.includes(query);

    return matchesFilter && matchesSearch;
  });

  const handleDownloadCalendar = () => {
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Kuwait Ophthalmic Society//Research Day 2026//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:kuwait-ophthalmology-research-day-2026@kos.org.kw',
      'SUMMARY:Kuwait Ophthalmology Program Research Day',
      'DESCRIPTION:Kuwait Ophthalmology Program Research Day featuring presentations on pediatric myopia, glaucoma, retinal surgery, keratoconus, cataract, and more. Venue: KIMS Theatre.',
      'LOCATION:KIMS Theatre, Kuwait Institute for Medical Specializations, Kuwait',
      'DTSTART:20261029T055000Z',
      'DTEND:20261029T100000Z',
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', 'Kuwait_Ophthalmology_Research_Day_2026.ics');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="rd-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="rd-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="rd-modal-close" onClick={onClose} aria-label="Close Announcement">
          ×
        </button>

        {/* Header with Organization Logos */}
        <div className="rd-modal-header">
          <div className="rd-logos-strip">
            <div className="rd-logo-wrap" title="Kuwait Medical Association">
              <img src={kmaLogo} alt="Kuwait Medical Association" className="rd-org-logo" />
              <span className="rd-org-name">الجمعية الطبية الكويتية</span>
            </div>
            <div className="rd-logo-wrap" title="Kuwait Ophthalmic Society">
              <img src={kosLogo} alt="Kuwait Ophthalmic Society" className="rd-org-logo" />
              <span className="rd-org-name">الرابطة الكويتية لطب العيون</span>
            </div>
            <div className="rd-logo-wrap" title="Kuwait Institute for Medical Specializations">
              <img src={kimsLogo} alt="K.I.M.S." className="rd-org-logo" />
              <span className="rd-org-name">معهد الكويت للاختصاصات الطبية</span>
            </div>
          </div>

          <div className="rd-header-badge">OFFICIAL ANNOUNCEMENT</div>
          <h2 className="rd-title">Kuwait Ophthalmology Program Research Day</h2>
          <p className="rd-arabic-title">يوم أبحاث برنامج طب وجراحة العيون</p>

          {/* Quick Info Badges */}
          <div className="rd-info-chips">
            <div className="rd-chip">
              <span className="rd-chip-icon">📅</span>
              <div className="rd-chip-text">
                <span className="rd-chip-label">Date</span>
                <span className="rd-chip-val">October 29th, 2026</span>
              </div>
            </div>
            <div className="rd-chip">
              <span className="rd-chip-icon">📍</span>
              <div className="rd-chip-text">
                <span className="rd-chip-label">Venue</span>
                <span className="rd-chip-val">KIMS Theatre</span>
              </div>
            </div>
            <div className="rd-chip">
              <span className="rd-chip-icon">⏰</span>
              <div className="rd-chip-text">
                <span className="rd-chip-label">Time</span>
                <span className="rd-chip-val">8:50 AM – 12:55 PM</span>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Bar: Filter, Search, Action buttons */}
        <div className="rd-controls-bar">
          <div className="rd-filter-tabs">
            <button
              className={`rd-tab-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Schedule ({RESEARCH_DAY_SCHEDULE.length})
            </button>
            <button
              className={`rd-tab-btn ${filter === 'presentations' ? 'active' : ''}`}
              onClick={() => setFilter('presentations')}
            >
              Scientific Papers
            </button>
            <button
              className={`rd-tab-btn ${filter === 'special' ? 'active' : ''}`}
              onClick={() => setFilter('special')}
            >
              Key Sessions
            </button>
          </div>

          <div className="rd-search-and-actions">
            <div className="rd-search-wrapper">
              <input
                type="text"
                placeholder="Search topic or presenter..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rd-search-input"
              />
              {searchQuery && (
                <button
                  className="rd-search-clear"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="rd-quick-actions">
              <a
                href="/KuwaitOphthalmologyProgramResearch2026.pdf"
                target="_blank"
                rel="noopener noreferrer"
                download="KuwaitOphthalmologyProgramResearch2026.pdf"
                className="rd-action-btn rd-btn-pdf"
                title="Download original PDF schedule"
              >
                📥 Download PDF
              </a>
              <button
                onClick={handleDownloadCalendar}
                className="rd-action-btn rd-btn-cal"
                title="Add event to calendar"
              >
                📅 Add to Calendar
              </button>
            </div>
          </div>
        </div>

        {/* Schedule List */}
        <div className="rd-schedule-body">
          <div className="rd-table-header">
            <div className="rd-th-time">Time</div>
            <div className="rd-th-title">Session / Scientific Presentation</div>
            <div className="rd-th-presenter">Presenter / Speaker</div>
          </div>

          <div className="rd-schedule-rows">
            {filteredSchedule.length === 0 ? (
              <div className="rd-no-results">
                <p>No presentations found matching &quot;{searchQuery}&quot;.</p>
                <button className="rd-btn-secondary" onClick={() => { setSearchQuery(''); setFilter('all'); }}>
                  Reset Filters
                </button>
              </div>
            ) : (
              filteredSchedule.map((item) => {
                const isBreak = item.type === 'break';
                const isSymposium = item.type === 'symposium';
                const isSpecial = item.type === 'special';

                return (
                  <div
                    key={item.id}
                    className={`rd-schedule-row ${isBreak ? 'rd-row-break' : ''} ${isSymposium ? 'rd-row-symposium' : ''
                      } ${isSpecial ? 'rd-row-special' : ''}`}
                  >
                    <div className="rd-cell-time">
                      <span className="rd-time-badge">{item.time}</span>
                    </div>

                    <div className="rd-cell-title">
                      <div className="rd-title-group">
                        {item.badge && <span className="rd-item-badge">{item.badge}</span>}
                        <h4 className="rd-item-title">{item.title}</h4>
                      </div>
                    </div>

                    <div className="rd-cell-presenter">
                      {isBreak ? (
                        <div className="rd-break-indicator">
                          <span>☕</span> {item.presenter}
                        </div>
                      ) : isSymposium ? (
                        <div className="rd-symposium-speaker">
                          <img src={bayerLogo} alt="Bayer" className="rd-bayer-logo" />
                          <span className="rd-speaker-name">Bayer Symposium</span>
                        </div>
                      ) : (
                        <div className="rd-speaker-profile">
                          <div className="rd-avatar-icon">👤</div>
                          <div>
                            <span className="rd-speaker-name">{item.presenter}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer Notes & Action Bar */}
        <div className="rd-modal-footer">
          <div className="rd-guidelines-note">
            <span className="rd-note-icon">📌</span>
            <div className="rd-note-text">
              <strong>Presentation Guidelines:</strong> Presentations should not exceed{' '}
              <strong>6–8 minutes</strong>, followed by <strong>2–4 minutes</strong> of discussion
              and questions (Q&A).
            </div>
          </div>

          <div className="rd-footer-actions">
            <a
              href="/KuwaitOphthalmologyProgramResearch2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="rd-btn-view-pdf"
            >
              📄 View Full Document (PDF)
            </a>
            <button className="rd-btn-close-modal" onClick={onClose}>
              Got It / Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchDayModal;
